import { sharedAccountManager } from './authConfig'
import { diagnosticError, diagnosticFetch, logDiagnostic, readDiagnosticJSON, recordGraphQLResult } from './diagnostics'
import { outlookBackendScopes } from './msalconfig'

const backendApiUrl = process.env.BACKEND_API_URL
const appsyncGraphqlEndpoint = process.env.APPSYNC_GRAPHQL_ENDPOINT

if (!backendApiUrl) {
    throw new Error('BACKEND_API_URL is not configured.')
}

if (!appsyncGraphqlEndpoint) {
    throw new Error('APPSYNC_GRAPHQL_ENDPOINT is not configured.')
}

export const BACKEND_API_URL = backendApiUrl
const APPSYNC_GRAPHQL_ENDPOINT = appsyncGraphqlEndpoint
const YOFFIX_TOKEN_STORAGE_KEY = 'yoffixToken'

const QUERY_GET_COMPANY_AND_EMPLOYEE = `
  query GetCompanyAndEmployee($id: ID!, $employeeId: ID!) {
    getCompany(id: $id) {
      id
      title
      Offices {
        items {
          id
          name
          workingFrom
          workingTo
          timeZone
        }
      }
    }
    getEmployee(id: $employeeId) {
      id
      officeID
      favouriteOfficeID
    }
  }
`

const QUERY_GET_SPACES_WITH_ROOMS = `
  query GetSpacesWithRooms($companyId: ID!, $type: ReservableType, $filter: ModelSpaceFilterInput) {
    listSpacesByCompanyAndName(companyID: $companyId, filter: $filter, sortDirection: ASC) {
      items {
        id
        name
        officeID
        workingHoursFrom
        workingHoursTo
        Reservables(filter: { type: { eq: $type } }, sortDirection: ASC, limit: 1000) {
          items {
            id
            name
            maxAmountOfPeople
            availabilityType
            externalRoomID
            relatedReservableID
            Tags {
              items {
                id
                Tag {
                  id
                  name
                }
              }
            }
            ReservableImages {
              items {
                name
                isMain
                location
              }
            }
            RelatedReservable {
              id
              name
            }
            Space {
              id
              name
              officeID
              workingHoursFrom
              workingHoursTo
            }
          }
        }
      }
    }
  }
`

const QUERY_GET_CALENDAR_EVENTS_BY_OFFICE = `
  query GetCalendarEventsByOffice(
    $officeId: ID!
    $startTime: ModelStringKeyConditionInput
    $filter: ModelCalendarEventsFilterInput
    $nextToken: String
  ) {
    listCalendarEventsByOfficeAndStartTime(
      officeID: $officeId
      startTime: $startTime
      filter: $filter
      sortDirection: ASC
      nextToken: $nextToken
    ) {
      items {
        id
        startTime
        endTime
        reservableID
      }
      nextToken
    }
  }
`

const QUERY_SEARCH_LIST_RESERVABLES_TAGS = `
  query ListReservableTagsByOfficeAndType(
    $filter: ModelReservableTagFilterInput!
    $officeID: ID!
    $reservableType: ModelStringKeyConditionInput
  ) {
    listReservableTagsByOfficeAndType(
      officeID: $officeID
      reservableType: $reservableType
      filter: $filter
      limit: 1000
    ) {
      items {
        id
        Tag {
          id
          name
        }
      }
    }
  }
`

const QUERY_GET_ALL_ROOM_MAPPINGS_NEW = `
  query GetAllRoomMappingsNew($input: GetRoomMappingsRequest) {
    getRoomsMapping(input: $input) {
      reservableRoomId
      roomId
      roomName
      roomEmail
    }
  }
`

const QUERY_GET_S3_LINK = `
  query GetS3Link(
    $operationType: S3OperationType!
    $path: String!
    $resize: ImageResizeInput
  ) {
    getS3SignedLink(operationType: $operationType, path: $path, resize: $resize)
  }
`

const QUERY_GET_RESERVABLE_WITH_AVAILABILITY = `
  query GetReservableWithAvailability(
    $employee: [EmployeesWithDepartments]
    $dates: [AWSDate]!
    $bookingRepeatType: RepeatType
    $daysOfWeek: [String]
    $startTime: String
    $endTime: String
    $intervalStart: AWSDateTime
    $intervalEnd: AWSDateTime
    $reservableId: ID!
  ) {
    getReservable(id: $reservableId) {
      id
      availabilityType
      ReservableToDepartments(limit: 1000) {
        items {
          Department {
            id
            __typename
          }
          availableDays
          disableAutoReleaseForTeamZoneSeats
          __typename
        }
        __typename
      }
      availabilityByDatesAndTime(
        bookingRepeatType: $bookingRepeatType
        employees: $employee
        dates: $dates
        daysOfWeek: $daysOfWeek
        startTime: $startTime
        endTime: $endTime
        intervalStart: $intervalStart
        intervalEnd: $intervalEnd
      )
      __typename
    }
  }
`

type GraphQLError = {
    errorType?: string
    message: string
}

type GraphQLResponse<T> = {
    data?: T
    errors?: GraphQLError[]
}

export type SessionClaims = {
    companyId: string
    employeeId: string
}

export type MicrosoftTokenClaims = {
    tenantId: string
}

export type CompanyBootstrap = {
    company: {
        id: string
        title: string
        Offices?: {
            items?: Array<{
                id: string
                name: string
                workingFrom?: string | null
                workingTo?: string | null
                timeZone?: string | null
            } | null> | null
        } | null
    }
    employee: {
        id: string
        officeID?: string | null
        favouriteOfficeID?: string | null
    }
}

export type RoomsResponse = {
    spaces: Array<{
        id: string
        name: string
        officeID?: string | null
        workingHoursFrom?: string | null
        workingHoursTo?: string | null
        Reservables?: {
            items?: Array<{
                id: string
                name?: string | null
                maxAmountOfPeople?: number | null
                externalRoomID?: string | null
                relatedReservableID?: string | null
                Tags?: {
                    items?: Array<{
                        Tag?: {
                            id?: string | null
                            name?: string | null
                        } | null
                    } | null> | null
                } | null
                ReservableImages?: {
                    items?: Array<{
                        location?: string | null
                        isMain?: boolean | null
                    } | null> | null
                } | null
            } | null> | null
        } | null
    }>
}

export type RoomEventsResponse = {
    items: Array<{
        id: string
        startTime: string
        endTime: string
        reservableID?: string | null
    }>
}

export type RoomTag = {
    id: string
    name: string
}

export type MicrosoftRoomMapping = {
    reservableRoomId: string
    roomId: string | null
    roomName: string | null
    roomEmail: string | null
}

export type ReservableAvailabilityRequest = {
    reservableId: string
    employeeId: string
    date: string
    dayOfWeek: string
    startTime: string
    endTime: string
    intervalStart: string
    intervalEnd: string
}

type S3ImageResizeInput = {
    fit: 'outside'
    targetHeight: number
    targetWidth: number
}

class SessionUnauthorizedError extends Error {
    constructor(message = 'Token has expired.') {
        super(message)
        this.name = 'SessionUnauthorizedError'
    }
}

let refreshSessionPromise: Promise<string> | null = null

function isAbsoluteUrl(value: string) {
    return /^https?:\/\//i.test(value)
}

function normalizeS3ImagePath(path: string) {
    const trimmedPath = path.trim()

    if (trimmedPath.startsWith('private/')) {
        return trimmedPath
    }

    return `private/${trimmedPath.replace(/^\/+/, '')}`
}

function parseJwtPayload(token: string): Record<string, string> {
    const payload = token.split('.')[1]

    if (!payload) {
        throw new Error('Invalid session token.')
    }

    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/')
    const decoded = atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '='))
    return JSON.parse(decoded) as Record<string, string>
}

export function readMicrosoftTokenClaims(token: string): MicrosoftTokenClaims {
    const payload = parseJwtPayload(token)
    const tenantId = payload.tid

    if (!tenantId) {
        throw new Error('Microsoft access token does not include tenant id.')
    }

    return { tenantId }
}

export function readSessionClaims(token: string): SessionClaims {
    const payload = parseJwtPayload(token)
    const companyId = payload['custom:companyId']
    const employeeId = payload['custom:employeeId']

    if (!companyId || !employeeId) {
        throw new Error('Session token does not include company or employee claims.')
    }

    return { companyId, employeeId }
}

export async function loginToBackend(msToken: string): Promise<string> {
    const res = await diagnosticFetch(
        `${BACKEND_API_URL}/auth/outlook/sso`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                microsoftAccessToken: msToken,
            }),
        },
        'outlookSso',
        'backend',
    )

    if (!res.ok) {
        throw new Error(`Backend login failed with status ${res.status}.`)
    }

    const data = await readDiagnosticJSON<{ token?: string }>(res)

    if (!data.token) {
        throw new Error('Backend login did not return a session token.')
    }

    sessionStorage.setItem(YOFFIX_TOKEN_STORAGE_KEY, data.token)
    return data.token as string
}

function getStoredSessionToken() {
    return sessionStorage.getItem(YOFFIX_TOKEN_STORAGE_KEY)
}

function resolveSessionToken(token?: string) {
    const storedToken = getStoredSessionToken()

    if (storedToken) {
        return storedToken
    }

    if (token) {
        sessionStorage.setItem(YOFFIX_TOKEN_STORAGE_KEY, token)
        return token
    }

    throw new Error('Backend authorization did not return a Yoffix session token.')
}

function isUnauthorizedGraphQLError(error: GraphQLError) {
    const normalizedMessage = error.message.toLowerCase()
    return error.errorType === 'UnauthorizedException' || normalizedMessage.includes('token has expired')
}

function toGraphQLRequestError(response: Response, fallbackMessage: string) {
    if (response.status === 401) {
        return new SessionUnauthorizedError()
    }

    return new Error(fallbackMessage)
}

function ensureGraphQLData<T>(result: GraphQLResponse<T>, fallbackMessage: string) {
    if (result.errors?.length) {
        const firstError = result.errors[0]

        if (isUnauthorizedGraphQLError(firstError)) {
            throw new SessionUnauthorizedError(firstError.message)
        }

        throw new Error(firstError.message)
    }

    if (!result.data) {
        throw new Error(fallbackMessage)
    }

    return result.data
}

async function refreshBackendSession() {
    if (!refreshSessionPromise) {
        refreshSessionPromise = (async () => {
            await sharedAccountManager.initialize()
            const microsoftAccessToken = await sharedAccountManager.ssoGetAccessToken(outlookBackendScopes)

            if (!microsoftAccessToken) {
                throw new Error('No Microsoft access token was returned.')
            }

            return loginToBackend(microsoftAccessToken)
        })().finally(() => {
            refreshSessionPromise = null
        })
    }

    return refreshSessionPromise
}

async function withSessionRefresh<T>(token: string | undefined, request: (sessionToken: string) => Promise<T>) {
    const currentToken = resolveSessionToken(token)

    try {
        return await request(currentToken)
    } catch (error) {
        if (!(error instanceof SessionUnauthorizedError)) {
            throw error
        }

        logDiagnostic('outlook_session_refresh_started')
        try {
            const refreshedToken = await refreshBackendSession()
            logDiagnostic('outlook_session_refresh_succeeded')
            return request(refreshedToken)
        } catch (refreshError) {
            logDiagnostic('outlook_session_refresh_failed', diagnosticError(refreshError), 'ERROR')
            throw refreshError
        }
    }
}

function summarizeVariables(variables: Record<string, unknown>) {
    const filter = variables.filter as { officeID?: { eq?: string } } | undefined
    const input = variables.input as { tenantId?: string } | undefined
    return {
        officeID:
            typeof variables.officeID === 'string'
                ? variables.officeID
                : typeof variables.officeId === 'string'
                  ? variables.officeId
                  : filter?.officeID?.eq || null,
        reservableID: typeof variables.reservableId === 'string' ? variables.reservableId : null,
        integrationID: input?.tenantId || null,
    }
}

async function graphQLRequest<T>(query: string, variables: Record<string, unknown>, token: string): Promise<T> {
    return withSessionRefresh(token, async (sessionToken) => {
        const response = await diagnosticFetch(
            APPSYNC_GRAPHQL_ENDPOINT,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: sessionToken,
                },
                body: JSON.stringify({
                    query,
                    variables,
                }),
            },
            query.match(/query\s+(\w+)/)?.[1] || 'unknown',
            'appsync',
            summarizeVariables(variables),
        )

        if (!response.ok) {
            throw toGraphQLRequestError(response, `GraphQL request failed with status ${response.status}.`)
        }

        const result = await readDiagnosticJSON<GraphQLResponse<T>>(response)
        recordGraphQLResult(result, response)
        return ensureGraphQLData(result, 'GraphQL request returned no data.')
    })
}

async function backendGraphQLRequest<T>(query: string, variables: Record<string, unknown>, token: string): Promise<T> {
    return withSessionRefresh(token, async (sessionToken) => {
        const response = await diagnosticFetch(
            `${BACKEND_API_URL}/graphql`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${sessionToken}`,
                },
                body: JSON.stringify({
                    query,
                    variables,
                }),
            },
            query.match(/query\s+(\w+)/)?.[1] || 'unknown',
            'backend',
            summarizeVariables(variables),
        )

        if (!response.ok) {
            throw toGraphQLRequestError(response, `Backend GraphQL request failed with status ${response.status}.`)
        }

        const result = await readDiagnosticJSON<GraphQLResponse<T>>(response)
        recordGraphQLResult(result, response)
        return ensureGraphQLData(result, 'Backend GraphQL request returned no data.')
    })
}

export async function fetchCompanyBootstrap(token: string, claims: SessionClaims): Promise<CompanyBootstrap> {
    const data = await graphQLRequest<{
        getCompany: CompanyBootstrap['company']
        getEmployee: CompanyBootstrap['employee']
    }>(QUERY_GET_COMPANY_AND_EMPLOYEE, { id: claims.companyId, employeeId: claims.employeeId }, token)

    return {
        company: data.getCompany,
        employee: data.getEmployee,
    }
}

export async function fetchRoomsForOffice(token: string, companyId: string, officeId: string): Promise<RoomsResponse> {
    const data = await graphQLRequest<{
        listSpacesByCompanyAndName?: {
            items?: RoomsResponse['spaces']
        } | null
    }>(
        QUERY_GET_SPACES_WITH_ROOMS,
        {
            companyId,
            type: 'ROOM',
            filter: {
                officeID: { eq: officeId },
            },
        },
        token,
    )

    return {
        spaces: data.listSpacesByCompanyAndName?.items?.filter(Boolean) || [],
    }
}

export async function fetchTagsForOffice(token: string, companyId: string, officeId: string): Promise<RoomTag[]> {
    const data = await graphQLRequest<{
        listReservableTagsByOfficeAndType?: {
            items?: Array<{
                Tag?: {
                    id?: string | null
                    name?: string | null
                } | null
            } | null>
        } | null
    }>(
        QUERY_SEARCH_LIST_RESERVABLES_TAGS,
        {
            officeID: officeId,
            reservableType: { eq: 'ROOM' },
            filter: {
                companyID: {
                    eq: companyId,
                },
            },
        },
        token,
    )

    const seen = new Set<string>()

    return (data.listReservableTagsByOfficeAndType?.items || [])
        .map((item) => item?.Tag)
        .filter((tag): tag is { id: string; name: string } => Boolean(tag?.id && tag?.name))
        .filter((tag) => {
            if (seen.has(tag.id)) {
                return false
            }

            seen.add(tag.id)
            return true
        })
}

export async function fetchCalendarEventsForOffice(
    token: string,
    officeId: string,
    date: string,
): Promise<RoomEventsResponse> {
    const items: RoomEventsResponse['items'] = []
    let nextToken: string | null = null

    do {
        const data = await graphQLRequest<{
            listCalendarEventsByOfficeAndStartTime?: {
                items?: RoomEventsResponse['items']
                nextToken?: string | null
            } | null
        }>(
            QUERY_GET_CALENDAR_EVENTS_BY_OFFICE,
            {
                officeId,
                startTime: { le: `${date}T23:59:00.000Z` },
                filter: { endTime: { ge: `${date}T00:00:00.000Z` } },
                nextToken,
            },
            token,
        )

        items.push(
            ...((data.listCalendarEventsByOfficeAndStartTime?.items || []).filter(
                Boolean,
            ) as RoomEventsResponse['items']),
        )
        nextToken = data.listCalendarEventsByOfficeAndStartTime?.nextToken || null
    } while (nextToken)

    return { items }
}

export async function fetchMicrosoftRoomMappings(token: string, tenantId: string): Promise<MicrosoftRoomMapping[]> {
    const data = await backendGraphQLRequest<{
        getRoomsMapping?: Array<{
            reservableRoomId?: string | null
            roomId?: string | null
            roomName?: string | null
            roomEmail?: string | null
        } | null> | null
    }>(
        QUERY_GET_ALL_ROOM_MAPPINGS_NEW,
        {
            input: {
                type: 'MICROSOFT',
                tenantId,
            },
        },
        token,
    )

    return (data.getRoomsMapping || [])
        .filter(Boolean)
        .filter(
            (
                mapping,
            ): mapping is {
                reservableRoomId?: string | null
                roomId?: string | null
                roomName?: string | null
                roomEmail?: string | null
            } => Boolean(mapping?.reservableRoomId),
        )
        .map((mapping) => ({
            reservableRoomId: mapping.reservableRoomId as string,
            roomId: mapping.roomId || null,
            roomName: mapping.roomName || null,
            roomEmail: mapping.roomEmail || null,
        }))
}

export async function fetchS3SignedLink(token: string, path: string, resize?: S3ImageResizeInput): Promise<string> {
    const normalizedPath = normalizeS3ImagePath(path)

    const data = await backendGraphQLRequest<{
        getS3SignedLink?: string | null
    }>(
        QUERY_GET_S3_LINK,
        {
            operationType: 'getObject',
            path: normalizedPath,
            resize,
        },
        token,
    )

    if (!data.getS3SignedLink) {
        throw new Error(`Signed S3 link was not returned for path "${normalizedPath}".`)
    }

    return data.getS3SignedLink
}

export async function fetchReservableAvailability(
    token: string,
    request: ReservableAvailabilityRequest,
): Promise<boolean> {
    const data = await graphQLRequest<{
        getReservable?: {
            availabilityByDatesAndTime?: boolean | null
        } | null
    }>(
        QUERY_GET_RESERVABLE_WITH_AVAILABILITY,
        {
            reservableId: request.reservableId,
            employee: [
                {
                    id: request.employeeId,
                    departmentIDs: [],
                },
            ],
            dates: [request.date],
            daysOfWeek: [request.dayOfWeek],
            startTime: request.startTime,
            endTime: request.endTime,
            intervalStart: request.intervalStart,
            intervalEnd: request.intervalEnd,
        },
        token,
    )

    return Boolean(data.getReservable?.availabilityByDatesAndTime)
}

export async function resolveRoomImageLinks(token: string, rooms: RoomsResponse): Promise<RoomsResponse> {
    const resize: S3ImageResizeInput = {
        fit: 'outside',
        targetHeight: 96,
        targetWidth: 96,
    }

    const uniquePaths = Array.from(
        new Set(
            rooms.spaces.flatMap((space) =>
                (space.Reservables?.items || []).flatMap((room) =>
                    (room?.ReservableImages?.items || [])
                        .map((image) => image?.location?.trim() || '')
                        .filter((location) => location.length > 0 && !isAbsoluteUrl(location)),
                ),
            ),
        ),
    )

    if (!uniquePaths.length) {
        return rooms
    }

    const resolvedEntries = await Promise.all(
        uniquePaths.map(async (path) => {
            try {
                const signedLink = await fetchS3SignedLink(token, path, resize)
                return [path, signedLink] as const
            } catch (error) {
                logDiagnostic(
                    'outlook_room_image_request_skipped',
                    { ...diagnosticError(error), reason: 'signing_failed_using_original_path' },
                    'WARN',
                )
                console.error(`Failed to resolve signed room image for "${normalizeS3ImagePath(path)}".`, error)
                return [path, path] as const
            }
        }),
    )

    const signedLinksByPath = new Map(resolvedEntries)

    return {
        spaces: rooms.spaces.map((space) => ({
            ...space,
            Reservables: space.Reservables
                ? {
                      ...space.Reservables,
                      items: (space.Reservables.items || []).map((room) =>
                          room
                              ? {
                                    ...room,
                                    ReservableImages: room.ReservableImages
                                        ? {
                                              ...room.ReservableImages,
                                              items: (room.ReservableImages.items || []).map((image) =>
                                                  image?.location
                                                      ? {
                                                            ...image,
                                                            location:
                                                                signedLinksByPath.get(image.location.trim()) ||
                                                                image.location,
                                                        }
                                                      : image,
                                              ),
                                          }
                                        : room.ReservableImages,
                                }
                              : room,
                      ),
                  }
                : space.Reservables,
        })),
    }
}
